import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import SEO from "../../components/SEO";
import Breadcrumbs, { buildBreadcrumbSchema } from "../../components/Breadcrumbs";
import { humanoidInfographicIndex } from "../../data/humanoid-infographics";

export default function HumanoidInfographicHub() {
  const [category, setCategory] = useState("");
  const categories = [...new Set(humanoidInfographicIndex.map((item) => item.category))].sort();
  const visible = useMemo(() => humanoidInfographicIndex.filter((item) => !category || item.category === category), [category]);
  const path = "/robotics/humanoid-robot-data";
  const breadcrumbs = [{ label: "Home", to: "/" }, { label: "Robotics", to: "/robotics" }, { label: "Humanoid robot data", to: path }];
  const schema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "Humanoid Robot Data, Charts and Industry Infographics",
    description: "Twenty source-linked humanoid robotics datasets covering price, hardware, availability, deployment, reliability, funding and training data.",
    url: `https://www.techniahqservice.com${path}`,
    hasPart: humanoidInfographicIndex.filter((item) => item.indexable).map((item) => ({ "@type": "TechArticle", name: item.title, url: `https://www.techniahqservice.com${item.path}` })),
  };

  return (
    <>
      <SEO title="Humanoid Robot Data and Industry Charts TechniaHQRobot" description="Explore 20 source-linked humanoid robot charts covering prices, hardware, availability, deployment, reliability, funding, components and training data." path={path} schema={[schema, buildBreadcrumbSchema(breadcrumbs)]} />
      <Breadcrumbs items={breadcrumbs} />
      <div>
        <section className="bg-neutral-950 py-14 text-white sm:py-20">
          <div className="site-shell">
            <p className="text-sm font-black uppercase tracking-[0.2em] text-blue-300">Source-linked humanoid robotics data</p>
            <h1 className="mt-4 max-w-5xl text-4xl font-black tracking-tight sm:text-6xl">Humanoid Robot Data, Charts and Industry Infographics</h1>
            <p className="mt-5 max-w-4xl text-lg leading-8 text-neutral-300">Twenty data pages separate documented values from undisclosed fields. Each chart preserves original units, original currencies, evidence status, source URLs and update dates.</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/humanoid-robots" className="btn-primary">Open humanoid catalog</Link>
              <Link to="/robot-types/humanoid-robots" className="rounded-full border border-white/25 px-5 py-3 text-sm font-black text-white hover:border-white">Read the humanoid guide</Link>
            </div>
          </div>
        </section>

        <section className="site-shell py-10 sm:py-14">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div><p className="text-xs font-black uppercase tracking-[0.18em] text-blue-700">Dataset directory</p><h2 className="mt-2 text-3xl font-black text-neutral-950">All infographic pages</h2></div>
            <label className="text-sm font-black text-neutral-700">Filter by category
              <select value={category} onChange={(event) => setCategory(event.target.value)} className="ml-3 rounded-xl border border-neutral-300 bg-white px-3 py-2 font-semibold">
                <option value="">All categories</option>{categories.map((item) => <option key={item} value={item}>{item}</option>)}
              </select>
            </label>
          </div>

          <div className="mt-8 grid gap-5 md:grid-cols-2 xl:grid-cols-3">
            {visible.map((item) => (
              <article key={item.slug} className="soft-card flex h-full flex-col p-6">
                <div className="flex flex-wrap items-center gap-2"><span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-black uppercase tracking-[0.14em] text-blue-700">{item.category}</span></div>
                <h3 className="mt-4 text-2xl font-black leading-tight text-neutral-950"><Link to={item.path} className="hover:text-blue-700">{item.title}</Link></h3>
                <p className="mt-3 flex-1 leading-7 text-neutral-600">{item.summary}</p>
                <dl className="mt-5 grid grid-cols-2 gap-3 rounded-2xl bg-neutral-50 p-4 text-sm">
                  <div><dt className="font-black text-neutral-500">Rows</dt><dd className="mt-1 font-bold text-neutral-950">{item.entryCount}</dd></div>
                  <div><dt className="font-black text-neutral-500">Updated</dt><dd className="mt-1 font-bold text-neutral-950">{item.lastUpdated}</dd></div>
                  <div className="col-span-2"><dt className="font-black text-neutral-500">Charts</dt><dd className="mt-1 text-neutral-700">{item.chartTypes.join(" · ")}</dd></div>
                  <div className="col-span-2"><dt className="font-black text-neutral-500">Primary source</dt><dd className="mt-1 text-neutral-700">{item.primarySource}</dd></div>
                </dl>
                <Link to={item.path} className="mt-5 inline-flex font-black text-blue-700 hover:underline">Open chart and data table</Link>
              </article>
            ))}
          </div>
        </section>
      </div>
    </>
  );
}
